import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {
  GridColDef,
  GridOverlay,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FuelCellRow, FuelCellSample, LOW_FUEL_THRESHOLD_PERCENT } from "../models/FuelCellRow";
import { FuelCellRowDetail } from "./FuelCellRowDetail";
import { I18nGrid } from "@/components/data/I18nGrid";

interface Props {
  rows: FuelCellRow[];
  samplesByDevice: Map<string, FuelCellSample[]>;
  isLoading: boolean;
  isFetching: boolean;
}

function FuelCellToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <Box sx={{ flex: 1 }} />
      <GridToolbarQuickFilter debounceMs={300} />
    </GridToolbarContainer>
  );
}

function NoDataOverlay() {
  const { t } = useTranslation();
  return (
    <GridOverlay>
      <Typography color="text.secondary">
        {t("fuelCell.noDevices", "No fuel cell devices found")}
      </Typography>
    </GridOverlay>
  );
}

function formatKg(value: number | null) {
  if (value == null) return "";
  return `${value.toFixed(2)} kg`;
}

function LevelBarCell({ value }: GridRenderCellParams<FuelCellRow, number | null>) {
  const theme = useTheme();
  if (value == null) return null;
  const clamped = Math.max(0, Math.min(100, value));
  const color =
    clamped < LOW_FUEL_THRESHOLD_PERCENT
      ? theme.palette.error.main
      : clamped < 40
        ? theme.palette.warning.main
        : theme.palette.success.main;
  return (
    <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 1,
          bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.25 : 0.15),
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: `${clamped}%`, height: "100%", bgcolor: color }} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 42, textAlign: "right" }}>
        {clamped.toFixed(0)} %
      </Typography>
    </Box>
  );
}

export function FuelCellGrid({ rows, samplesByDevice, isLoading, isFetching }: Props) {
  const { t, i18n } = useTranslation();

  const columns: GridColDef<FuelCellRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t("fuelCell.name", "Vehicle"),
        flex: 1.4,
        minWidth: 160,
      },
      {
        field: "licensePlate",
        headerName: t("fuelCell.licensePlate", "License plate"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "vin",
        headerName: t("vin", "VIN"),
        flex: 1.2,
        minWidth: 160,
      },
      {
        field: "tankCapacityKg",
        headerName: t("fuelCell.tankCapacityKg", "Tank capacity (kg)"),
        type: "number",
        flex: 1,
        minWidth: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatKg(value),
      },
      {
        field: "levelPercent",
        headerName: t("fuelCell.levelPercent", "Level"),
        type: "number",
        flex: 1.2,
        minWidth: 160,
        renderCell: params => <LevelBarCell {...params} />,
      },
      {
        field: "remainingKg",
        headerName: t("fuelCell.remainingKg", "Remaining (kg)"),
        type: "number",
        flex: 1,
        minWidth: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatKg(value),
      },
      {
        field: "usedKg",
        headerName: t("fuelCell.usedKg", "Used (kg)"),
        type: "number",
        flex: 1,
        minWidth: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatKg(value),
      },
      {
        field: "usedRangeKg",
        headerName: t("fuelCell.usedRangeKg", "Used in range (kg)"),
        type: "number",
        flex: 1,
        minWidth: 150,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => formatKg(value),
      },
      {
        field: "refillCount",
        headerName: t("fuelCell.refillCount", "Refills"),
        type: "number",
        flex: 0.7,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) => (value == null ? "" : String(value)),
      },
      {
        field: "distanceKm",
        headerName: t("fuelCell.distanceKm", "Distance (km)"),
        type: "number",
        flex: 1,
        minWidth: 130,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) =>
          value == null ? "" : `${value.toFixed(0)} km`,
      },
      {
        field: "avgConsumptionKgPer100Km",
        headerName: t("fuelCell.avgConsumptionKgPer100Km", "Avg consumption (kg/100km)"),
        type: "number",
        flex: 1.1,
        minWidth: 170,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number | null) =>
          value == null ? "" : `${value.toFixed(2)} kg/100km`,
      },
      {
        field: "updatedAt",
        headerName: t("fuelCell.updatedAt", "Last update"),
        type: "dateTime",
        flex: 1.2,
        minWidth: 170,
        valueGetter: (value: Date | null) => (value ? new Date(value) : null),
        valueFormatter: (value: Date | null) =>
          value
            ? value.toLocaleString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "",
      },
    ],
    [t, i18n.language],
  );

  return (
    <Box sx={{ flex: 1, minHeight: 640, position: "relative" }}>
      {isFetching && !isLoading && (
        <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }} />
      )}
      <I18nGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        density="compact"
        initialState={{
          sorting: { sortModel: [{ field: "name", sort: "asc" }] },
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        getDetailPanelHeight={() => 320}
        getDetailPanelContent={({ row }) => (
          <FuelCellRowDetail row={row} samples={samplesByDevice.get(row.id) ?? []} />
        )}
        slots={{
          toolbar: FuelCellToolbar,
          noRowsOverlay: NoDataOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress" as const,
            noRowsVariant: "skeleton" as const,
          },
        }}
      />
    </Box>
  );
}
